import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from '../../../config';

const TIME_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '1d',  label: '1D' },
  { value: '3d',  label: '3D' },
  { value: '7d',  label: '7D' },
  { value: '1m',  label: '1M' },
  { value: '3m',  label: '3M' },
];

const HORIZONS = [
  { value: 'current', label: 'Live' },
  { value: '7d',      label: '7D' },
  { value: '1m',      label: '1M' },
  { value: '3m',      label: '3M' },
  { value: '6m',      label: '6M' },
  { value: '1y',      label: '1Y' },
];

const ORDER_OPTIONS = [
  { value: '-analyst_count', label: 'Analysts' },
  { value: '-prediction_count', label: 'Calls' },
  { value: '-bullish_count', label: 'Bullish' },
  { value: '-bearish_count', label: 'Bearish' },
];

const RANK_OPTIONS = [
  { value: 'overall',     label: 'Overall' },
  { value: 'return',      label: 'Return' },
  { value: 'predictions', label: 'Predictions' },
  { value: 'followers',   label: 'Followers' },
];

function SentimentBar({ bullish, bearish, neutral }) {
  const total = (bullish ?? 0) + (bearish ?? 0) + (neutral ?? 0);
  if (!total) return null;
  const bPct = Math.round((bullish / total) * 100);
  const rPct = Math.round((bearish / total) * 100);
  return (
    <div className="flex h-1 rounded-full overflow-hidden w-full mt-1.5">
      <div className="bg-green-500" style={{ width: `${bPct}%` }} />
      <div className="bg-gray-500 flex-1" />
      <div className="bg-red-500" style={{ width: `${rPct}%` }} />
    </div>
  );
}

function Avatar({ url, name, size = 7 }) {
  const [err, setErr] = useState(false);
  const sz = `w-${size} h-${size}`;
  if (url && !err) {
    return (
      <img src={url} alt={name} onError={() => setErr(true)}
        className={`${sz} rounded-full object-cover flex-shrink-0`} />
    );
  }
  return (
    <div className={`${sz} rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-gray-200`}>
      {name?.[0]?.toUpperCase() ?? '?'}
    </div>
  );
}

export default function SymbolsTab({ onSelectSymbol, overlayState, onOverlayChange }) {
  const [view, setView] = useState('list');
  const [activeSymbol, setActiveSymbol] = useState(null);

  // Symbol list state
  const [symbols, setSymbols] = useState([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [search, setSearch] = useState('');
  const [since, setSince] = useState('all');
  const [ordering, setOrdering] = useState('-analyst_count');
  const [industry, setIndustry] = useState('');
  const [industries, setIndustries] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Analyst view state
  const [analysts, setAnalysts] = useState([]);
  const [loadingAnalysts, setLoadingAnalysts] = useState(false);
  const [analystSearch, setAnalystSearch] = useState('');
  const [horizon, setHorizon] = useState('current');
  const [rankBy, setRankBy] = useState('overall');

  useEffect(() => {
    setPage(1);
    setSymbols([]);
  }, [search, since, ordering, industry]);

  // Fetch industry options once
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/research/symbols/filter-options/`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(d => setIndustries(d.industries ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingSymbols(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ page, page_size: 20, ordering });
        if (search) params.set('search', search);
        if (since !== 'all') params.set('since', since);
        if (industry) params.set('industry', industry);
        const res = await fetch(`${API_BASE_URL}/api/research/symbols/?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const results = data.results ?? data ?? [];
        if (!cancelled) {
          setSymbols(prev => page === 1 ? results : [...prev, ...results]);
          setHasMore(!!data.next);
        }
      } catch {
        if (!cancelled) setSymbols([]);
      } finally {
        if (!cancelled) setLoadingSymbols(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [page, search, since, ordering, industry]);

  const fetchAnalysts = useCallback(async (exchange, ticker) => {
    setLoadingAnalysts(true);
    setAnalysts([]);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE_URL}/api/research/symbols/${exchange}/${ticker}/predictions/?page_size=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const results = data.results ?? data ?? [];
      const map = new Map();
      for (const p of results) {
        if (!map.has(p.analyst_slug)) {
          map.set(p.analyst_slug, {
            slug: p.analyst_slug,
            name: p.analyst_name,
            imageUrl: p.analyst_image_url,
            followers: p.analyst_twitter_followers,
            sidebarRank: p.analyst_sidebar_rank ?? 0,
            firstCallReturn: parseFloat(p.analyst_first_call_return) || 0,
            predictionsCount: p.analyst_predictions_count ?? 0,
            returnsByHorizon: { current: [], '7d': [], '1m': [], '3m': [], '6m': [], '1y': [] },
          });
        }
        const rb = map.get(p.analyst_slug).returnsByHorizon;
        const push = (key, val) => { const v = parseFloat(val); if (!isNaN(v)) rb[key].push(v); };
        push('current', p.current_return_pct);
        push('7d', p.return_at_7d);
        push('1m', p.return_at_1m);
        push('3m', p.return_at_3m);
        push('6m', p.return_at_6m);
        push('1y', p.return_at_1y);
      }
      const avg = arr => arr.length ? arr.reduce((s, r) => s + r, 0) / arr.length : null;
      const unique = Array.from(map.values()).map(({ returnsByHorizon, ...a }) => ({
        ...a,
        returnsByHorizon,
        avgReturn: avg(returnsByHorizon.current),
      }));
      setAnalysts(unique);
    } catch {
      setAnalysts([]);
    } finally {
      setLoadingAnalysts(false);
    }
  }, []);

  const handleSymbolClick = (sym) => {
    // sym.ticker may be the full "EXCHANGE:SYMBOL" string; sym.symbol is the bare symbol
    const fullTicker = sym.ticker || `${sym.exchange}:${sym.symbol}`;
    const [exchange, symbolPart] = fullTicker.includes(':')
      ? fullTicker.split(':', 2)
      : [sym.exchange, fullTicker];
    setActiveSymbol({ exchange, ticker: symbolPart, name: sym.name });
    setView('analysts');
    setAnalystSearch('');
    setHorizon('current');
    setRankBy('overall');
    onSelectSymbol?.(`${exchange}:${symbolPart}`);
    onOverlayChange({ selectedAnalysts: [], direction: 'all' });
    fetchAnalysts(exchange, symbolPart);
  };

  const handleBack = () => {
    setView('list');
    onOverlayChange({ selectedAnalysts: [], direction: 'all' });
  };

  const toggleAnalyst = (slug) => {
    const current = overlayState.selectedAnalysts ?? [];
    const next = current.includes(slug)
      ? current.filter(s => s !== slug)
      : [...current, slug];
    onOverlayChange({ ...overlayState, selectedAnalysts: next });
  };

  const clearAll = () => onOverlayChange({ ...overlayState, selectedAnalysts: [] });

  // ── Analyst filter view ──────────────────────────────────────────────────
  if (view === 'analysts' && activeSymbol) {
    const selected = overlayState.selectedAnalysts ?? [];
    const decorated = analysts.map(a => {
      const rb = a.returnsByHorizon?.[horizon] ?? [];
      const avgReturn = rb.length ? rb.reduce((s, r) => s + r, 0) / rb.length : null;
      return { ...a, avgReturn };
    });
    const sortKey = {
      overall:     a => a.sidebarRank ?? 0,
      return:      a => a.avgReturn ?? -Infinity,
      predictions: a => a.predictionsCount ?? 0,
      followers:   a => a.followers ?? 0,
    }[rankBy];
    const filtered = decorated
      .filter(a => !analystSearch || a.name?.toLowerCase().includes(analystSearch.toLowerCase()))
      .sort((a, b) => sortKey(b) - sortKey(a));

    return (
      <div className="flex flex-col h-full">
        {/* Symbol header + back */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-700 flex-shrink-0">
          <button onClick={handleBack}
            className="text-gray-400 hover:text-gray-200 transition-colors p-0.5 rounded hover:bg-gray-700">
            <ArrowLeft size={15} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-100">{activeSymbol.ticker}
              <span className="ml-1.5 text-xs text-gray-500 font-normal">{activeSymbol.exchange}</span>
            </div>
            {activeSymbol.name && (
              <div className="text-xs text-gray-500 truncate">{activeSymbol.name}</div>
            )}
          </div>
        </div>

        {/* Search + count */}
        <div className="p-3 space-y-2 border-b border-gray-700 flex-shrink-0">
          <input type="text" placeholder="Search analysts..."
            value={analystSearch} onChange={e => setAnalystSearch(e.target.value)}
            className="w-full bg-gray-700 text-gray-200 placeholder-gray-500 text-sm rounded-md px-3 py-1.5 border border-gray-600 focus:outline-none focus:border-blue-500" />
          {/* Ranking buttons + horizon dropdown */}
          <div className="flex gap-1 items-center">
            {RANK_OPTIONS.map(r => (
              <button key={r.value} onClick={() => setRankBy(r.value)}
                title={`Rank by ${r.label.toLowerCase()}`}
                className={`flex-1 py-1 text-[11px] rounded transition-colors ${
                  rankBy === r.value
                    ? 'bg-blue-600 text-white font-medium'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}>
                {r.label}
              </button>
            ))}
            <select
              value={horizon}
              onChange={e => setHorizon(e.target.value)}
              title="Return horizon — average % return per analyst on this symbol"
              className="bg-gray-700 text-gray-300 text-[11px] rounded px-1.5 py-1 outline-none border border-transparent hover:bg-gray-600 focus:border-blue-500"
            >
              {HORIZONS.map(h => (
                <option key={h.value} value={h.value}>{h.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{selected.length > 0 ? `${selected.length} selected` : 'Select analysts to show marks'}</span>
            <div className="flex items-center gap-2">
              {filtered.length > 0 && selected.length < filtered.length && (
                <button
                  onClick={() => onOverlayChange({ ...overlayState, selectedAnalysts: filtered.map(a => a.slug) })}
                  className="text-blue-400 hover:text-blue-300">Select all</button>
              )}
              {selected.length > 0 && (
                <button onClick={clearAll} className="text-blue-400 hover:text-blue-300">Clear all</button>
              )}
            </div>
          </div>
        </div>

        {/* Analyst list */}
        <div className="flex-1 overflow-y-auto">
          {loadingAnalysts && (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loadingAnalysts && filtered.length === 0 && (
            <p className="text-xs text-gray-500 text-center py-8 px-4">No analysts found</p>
          )}
          {!loadingAnalysts && filtered.map(analyst => {
            const isSelected = selected.includes(analyst.slug);
            const avgReturn = analyst.avgReturn;
            return (
              <button key={analyst.slug} onClick={() => toggleAnalyst(analyst.slug)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-gray-700/50 transition-colors ${
                  isSelected ? 'bg-blue-900/30' : 'hover:bg-gray-700/40'
                }`}>
                <div className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-500'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <Avatar url={analyst.imageUrl} name={analyst.name} size={7} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-200 truncate">{analyst.name}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {analyst.followers && (
                      <span>{(analyst.followers / 1000).toFixed(0)}K followers</span>
                    )}
                    {avgReturn !== null && (
                      <span className={avgReturn >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {avgReturn >= 0 ? '+' : ''}{avgReturn.toFixed(1)}% avg
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom controls: direction filter + aggregate toggle */}
        {selected.length > 0 && (
          <div className="p-3 border-t border-gray-700 flex-shrink-0 space-y-2.5">
            <div>
              <div className="text-xs text-gray-400 mb-1.5">Direction filter</div>
              <div className="flex rounded-md overflow-hidden border border-gray-600 text-xs">
                {['all', 'bullish', 'bearish'].map(d => (
                  <button key={d} onClick={() => onOverlayChange({ ...overlayState, direction: d })}
                    className={`flex-1 py-1.5 capitalize transition-colors ${
                      overlayState.direction === d ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => onOverlayChange({ ...overlayState, aggregateOnBar: !overlayState.aggregateOnBar })}
              className="w-full flex items-center justify-between px-2.5 py-2 rounded-md border border-gray-600 bg-gray-700/50 hover:bg-gray-700 transition-colors"
            >
              <div className="text-left">
                <div className="text-xs text-gray-200">Aggregate on Bar</div>
                <div className="text-[10px] text-gray-500 mt-0.5">One icon per analyst per bar, sized by call count</div>
              </div>
              <div className={`w-8 h-4 rounded-full flex-shrink-0 ml-2 transition-colors relative ${
                overlayState.aggregateOnBar ? 'bg-blue-600' : 'bg-gray-600'
              }`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                  overlayState.aggregateOnBar ? 'translate-x-4' : 'translate-x-0.5'
                }`} />
              </div>
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Symbol list view ─────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-2 border-b border-gray-700 flex-shrink-0">
        <input type="text" placeholder="Search symbol..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full bg-gray-700 text-gray-200 placeholder-gray-500 text-sm rounded-md px-3 py-1.5 border border-gray-600 focus:outline-none focus:border-blue-500" />
        <div className="flex gap-1.5">
          <select value={since} onChange={e => setSince(e.target.value)}
            className="flex-1 bg-gray-700 text-gray-300 text-xs rounded px-2 py-1 border border-gray-600 focus:outline-none">
            {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={ordering} onChange={e => setOrdering(e.target.value)}
            className="flex-1 bg-gray-700 text-gray-300 text-xs rounded px-2 py-1 border border-gray-600 focus:outline-none">
            {ORDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <select value={industry} onChange={e => setIndustry(e.target.value)}
          className="w-full bg-gray-700 text-gray-300 text-xs rounded px-2 py-1 border border-gray-600 focus:outline-none">
          <option value="">All Industries</option>
          {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {symbols.map(sym => (
          <button key={sym.ticker || `${sym.exchange}:${sym.symbol}`} onClick={() => handleSymbolClick(sym)}
            className="w-full px-3 py-2.5 border-b border-gray-700/50 hover:bg-gray-700/40 text-left transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-100">{sym.ticker}</span>
                <span className="text-xs text-gray-500 bg-gray-700 rounded px-1">{sym.exchange}</span>
              </div>
              <span className="text-xs text-gray-400">{sym.analyst_count ?? 0} analysts</span>
            </div>
            {sym.name && <div className="text-xs text-gray-500 truncate mt-0.5">{sym.name}</div>}
            <SentimentBar bullish={sym.bullish_count} bearish={sym.bearish_count} neutral={sym.neutral_count} />
          </button>
        ))}

        {loadingSymbols && (
          <div className="flex justify-center py-4">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!loadingSymbols && hasMore && (
          <button onClick={() => setPage(p => p + 1)}
            className="w-full py-2.5 text-xs text-blue-400 hover:text-blue-300 border-t border-gray-700">
            Load more
          </button>
        )}
        {!loadingSymbols && symbols.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-8">No symbols found</p>
        )}
      </div>
    </div>
  );
}
